import { bookingIdToDiseaseId } from 'constants/diseases'

import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { parse } from 'qs'
import { get } from 'lodash'
import { Link } from 'react-router-dom'
import Container from 'components/Container'
import LoginButton from 'components/LoginButton'
import Message from 'components/Message'
import Button from 'components/Button'
import Section from 'components/Section'
import Cover from 'components/Cover'
import Spinner from 'components/Spinner'
import useOrgUnit from 'hooks/useOrgUnit'
import useSystemStatusMessage from 'hooks/useSystemStatusMessage'
import Header from 'apps/booking/components/Header'
import useSession from 'hooks/useSession'

import Footer from '../components/Footer'

export default function LoginPage({ orgUnit: orgUnitSlug }) {
  const [error, setError] = useState(false)
  const { bookingSystemMessage } = useSystemStatusMessage()
  const { isSignedIn } = useSession()
  const { loading: ouLoading, ou: orgUnit } = useOrgUnit(orgUnitSlug)

  const title = get(orgUnit, 'title', false) || false
  const types = get(orgUnit, 'features', false) || false
  const vaccinationProperties = get(orgUnit, 'vaccination_properties', {})
  const isHospital = orgUnit?.isHospital

  const isPublicBookingOpen = (bookingId) =>
    vaccinationProperties[bookingIdToDiseaseId(bookingId)]?.public_booking_enabled ?? false

  useEffect(() => {
    const string = window.location.search
    const parsed = parse(string, { ignoreQueryPrefix: true })

    if (parsed.failed) {
      setError(true)
    }
  }, [])

  if (ouLoading) {
    return <Spinner />
  }

  if (!title) {
    return (
      <>
        <Header />
        <Container size="tight">
          <h1>Vi fant ikke denne siden</h1>
          <p>
            Er du sikker på at du har brukt riktig lenke? Vennligst gå tilbake til start og prøv
            igjen.
          </p>
          <Button arrow to="/">
            Gå til start
          </Button>
        </Container>
      </>
    )
  }

  const testTypeOption = {
    COVID_VACCINE: (
      <Section key="COVID_VACCINE" title="Koronavaksine" type="box">
        {isHospital ? (
          <p>Her kan du fylle ut egenerklæringen før vaksinasjon.</p>
        ) : (
          <p>
            Her kan du bestille et tidspunkt for vaksine i din kommune eller fylle ut
            egenerklæringen før vaksinasjon.
          </p>
        )}

        {isSignedIn ? (
          <Button primary href={`/${orgUnit.slug}/koronavaksine`}>
            Gå videre
          </Button>
        ) : (
          <LoginButton
            idp="idporten"
            primary
            state={`booking:${orgUnit.slug}:COVID_VACCINE`}
            redirectPath={`/${orgUnit.slug}/koronavaksine`}
          >
            Logg inn
          </LoginButton>
        )}
      </Section>
    ),
    COVID_TEST: (
      <Section key="COVID_TEST" title="Koronatest" type="box">
        <p>
          Her kan du bestille et tidspunkt for en COVID-19 test. For deg med symptomer eller
          mistanke om smitte som ønsker å teste deg for koronasmitte.
        </p>
        {isSignedIn ? (
          <Button primary href={`/${orgUnit.slug}/koronatest`}>
            Gå videre
          </Button>
        ) : (
          <LoginButton
            idp="idporten"
            primary
            state={`booking:${orgUnit.slug}:COVID_TEST`}
            redirectPath={`/${orgUnit.slug}/koronatest`}
          >
            Logg inn
          </LoginButton>
        )}
      </Section>
    ),
    FLU_VACCINE: (
      <Section key="FLU_VACCINE" title="Influensavaksine" type="box">
        <p>
          Her kan du bestille et tidspunkt for å få vaksine på en av kommunens helseinstitusjoner.
        </p>
        {isSignedIn ? (
          <Button primary href={`/${orgUnit.slug}/influensavaksine`}>
            Gå videre
          </Button>
        ) : (
          <LoginButton
            idp="idporten"
            primary
            state={`booking:${orgUnit.slug}:FLU_VACCINE`}
            redirectPath={`/${orgUnit.slug}/influensavaksine`}
          >
            Logg inn
          </LoginButton>
        )}
      </Section>
    ),
  }

  const description = isHospital ? <br /> : 'For deg som bor eller oppholder deg i kommunen.'

  return (
    <>
      {bookingSystemMessage && (
        <Message type="notice" spaceless>
          {bookingSystemMessage}
        </Message>
      )}
      <Header seoTitle={`${title}`} seoDescription={description} />
      <main style={{ paddingBottom: '3rem' }}>
        <Cover title={`${title}`} size="tight" preamble={description} />

        <Container size="tight">
          {title && orgUnit.slug === 'reminneapolis' && (
            <Message type="notice">
              Denne siden er kun ment til testing og gir deg ikke en ekte timebestilling. Vennligst
              gå <Link to="/">tilbake og velg en tilgjengelig kommune</Link>.
            </Message>
          )}
          {types.filter(isPublicBookingOpen).map((type) => testTypeOption[type] || null)}
          {error && <Message type="warning">Logg inn feilet</Message>}

          <Footer />
        </Container>
      </main>
    </>
  )
}

LoginPage.propTypes = {
  redirectUrl: T.string,
  orgUnit: T.string.isRequired,
}
